import type { Commit, SessionState } from "../types";
import { getStoredApiKey } from "./apiKey";

export interface CreateSessionRequest {
  diners: string[];
  currency: string;
}

export interface CreateSessionResponse {
  sessionId: string;
  state: SessionState;
}

const API_BASE_URL = "https://billsplitapi.kernmail.eu";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = await response
      .json()
      .catch(() => ({ error: response.statusText }));
    const detail =
      typeof message === "object" && message && "error" in message
        ? (message.error as string)
        : response.statusText;
    throw new Error(detail || "Request failed");
  }
  return response.json() as Promise<T>;
};

const buildUrl = (path: string): string => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalized}`;
};

const request = async <T>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const apiKey = getStoredApiKey();
  if (!apiKey) {
    throw new Error("Missing API key");
  }
  const headers = new Headers(init.headers ?? {});
  headers.set("Authorization", `Bearer ${apiKey}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(buildUrl(path), {
    ...init,
    headers,
  });
  return handleResponse<T>(response);
};

export const fetchFrequentDiners = async (): Promise<string[]> => {
  const data = await request<{ diners: string[] }>("/api/frequent-diners");
  return data.diners ?? [];
};

export const createSession = async (
  payload: CreateSessionRequest
): Promise<CreateSessionResponse> => {
  return request<CreateSessionResponse>("/api/sessions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const fetchSessionState = async (
  sessionId: string
): Promise<SessionState> => {
  return request<SessionState>(`/api/sessions/${sessionId}`);
};

export const fetchSessionMeta = async (
  sessionId: string
): Promise<{ updatedAt: string }> => {
  return request<{ updatedAt: string }>(`/api/sessions/${sessionId}/meta`);
};

export const applyCommit = async (
  sessionId: string,
  commit: Commit
): Promise<SessionState> => {
  return request<SessionState>(`/api/sessions/${sessionId}/commits`, {
    method: "POST",
    body: JSON.stringify(commit),
  });
};

export const validateApiKey = async (key: string): Promise<void> => {
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${key}`);
  const response = await fetch(`${API_BASE_URL}/healthz`, {
    headers,
    method: "GET",
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      if (body && typeof body === "object" && "error" in body) {
        detail = String(body.error);
      }
    } catch (error) {
      // ignore JSON parsing errors
    }
    throw new Error(detail || "Failed to validate API key");
  }
};
